import React from "react";
import type { Player } from "../../../shared/models/Player";
import TeamLogo from "../../../shared/components/TeamLogo";
import { formatTeamAbbreviation } from "../../../shared/utils/teamAbbreviation";

function MobileRankingsBoardCardComponent(props: {
  pickLabel: string;
  directionArrow: string;
  player?: Player;
  favorite?: boolean;
  drafted?: boolean;
  bg: string;
  marginRight?: number;
  tabletMode?: boolean;
  dimDrafted?: boolean;
  ownerLabel?: string;
  ownerIsTraded?: boolean;
  onOpenProfile?: (id: string) => void;
  onOpenAssign?: () => void;
  shouldSuppressTap?: () => boolean;
}) {
  const { pickLabel, directionArrow, player, favorite = false, drafted = false, bg, marginRight = 3, tabletMode = false, dimDrafted = true, ownerLabel, ownerIsTraded = false, onOpenProfile, onOpenAssign, shouldSuppressTap } = props;
  const visibleOwnerLabel = ownerIsTraded ? ownerLabel : undefined;
  const visibleOwnerText = React.useMemo(() => (visibleOwnerLabel ?? "").trim().slice(0, 5), [visibleOwnerLabel]);
  const ownerBadgeIsArrow = Boolean(visibleOwnerLabel);
  const displayTeam = React.useMemo(() => {
    return formatTeamAbbreviation(player?.team, "FA");
  }, [player?.team]);
  const nameLines = React.useMemo(() => {
    if (!player?.name) return ["", ""];
    const parts = player.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return [parts[0] ?? "", ""];
    const suffixes = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v"]);
    const lastPart = parts[parts.length - 1] ?? "";
    const normalizedLastPart = lastPart.toLowerCase();

    if (parts.length >= 3 && suffixes.has(normalizedLastPart)) {
      return [parts.slice(0, -2).join(" "), parts.slice(-2).join(" ")];
    }

    return [parts.slice(0, -1).join(" "), lastPart];
  }, [player?.name]);

  return (
    <button
      type="button"
      onClick={() => {
        if (shouldSuppressTap?.()) return;
        if (player) {
          onOpenProfile?.(player.id);
          return;
        }
        onOpenAssign?.();
      }}
      style={{
        appearance: "none",
        border: "none",
        background: "transparent",
        boxSizing: "border-box",
        width: tabletMode ? 91 : 72,
        minWidth: tabletMode ? 91 : 72,
        height: tabletMode ? 64 : 48,
        marginRight,
        padding: tabletMode ? 5 : 4,
        position: "relative",
        overflow: "hidden",
        opacity: drafted && dimDrafted ? 0.55 : 1,
        filter: drafted && dimDrafted ? "grayscale(18%)" : undefined,
        cursor: player ? (onOpenProfile ? "pointer" : "default") : onOpenAssign ? "pointer" : "default",
        textAlign: "left",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 5,
          right: 0,
          bottom: 5,
          left: 0,
          borderRadius: 7,
          border: "1px solid rgba(255,255,255,0.12)",
          outline: "1px solid rgba(0,0,0,0.18)",
          background: bg,
          pointerEvents: "none",
        }}
      />

      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 5,
          right: 0,
          bottom: 5,
          left: 0,
          background: "rgba(0,0,0,0.1)",
          pointerEvents: "none",
          borderRadius: 7,
        }}
      />

      {favorite ? (
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 4,
            right: 5,
            color: "#fbbf24",
            fontSize: 11,
            lineHeight: 1,
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          ★
        </div>
      ) : null}

      <div
        style={{
          position: "relative",
          zIndex: 1,
          height: "100%",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: player ? 0 : tabletMode ? 5 : 4,
            right: favorite ? (tabletMode ? 14 : 12) : 1,
            fontSize: tabletMode ? 7 : 6,
            lineHeight: 1,
            fontWeight: 700,
            color: "rgba(255,255,255,0.84)",
            letterSpacing: -0.1,
            textAlign: "right",
          }}
        >
          {pickLabel}
        </div>

        {visibleOwnerLabel ? (
          <div
            title={visibleOwnerLabel}
            style={{
              position: "absolute",
              top: ownerBadgeIsArrow ? undefined : player ? (tabletMode ? 0 : 0) : tabletMode ? 5 : 4,
              right: ownerBadgeIsArrow ? (tabletMode ? -1 : -2) : undefined,
              bottom: ownerBadgeIsArrow ? (player ? (tabletMode ? 10 : 9) : tabletMode ? 6 : 5) : undefined,
              left: ownerBadgeIsArrow ? undefined : 0,
              maxWidth: ownerBadgeIsArrow ? (tabletMode ? 42 : 36) : tabletMode ? 48 : 38,
              minHeight: ownerBadgeIsArrow ? (tabletMode ? 12 : 10) : undefined,
              padding: ownerBadgeIsArrow ? (tabletMode ? "2px 4px" : "1px 3px") : "1px 3px",
              borderRadius: ownerBadgeIsArrow ? 999 : 4,
              border: ownerIsTraded ? "1px solid rgb(255,215,0)" : "1px solid rgba(255,255,255,0.10)",
              background: ownerIsTraded ? "rgb(168, 106, 20)" : "rgba(0,0,0,0.14)",
              color: ownerIsTraded ? "rgb(255, 255, 238)" : "rgba(255,255,255,0.72)",
              fontSize: tabletMode ? 6 : 5,
              lineHeight: 1,
              fontWeight: 800,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              letterSpacing: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: ownerBadgeIsArrow ? 2 : 0,
              zIndex: 3,
              boxShadow: ownerBadgeIsArrow ? "0 1px 2px rgba(0,0,0,0.42)" : undefined,
            }}
          >
            <span
              style={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {visibleOwnerText}
            </span>
            {ownerBadgeIsArrow ? <span aria-hidden>{directionArrow}</span> : null}
          </div>
        ) : null}

        {ownerBadgeIsArrow ? null : (
          <div
            style={{
              position: "absolute",
              right: favorite ? (tabletMode ? 11 : 9) : 0,
              bottom: player ? (tabletMode ? 10 : 9) : tabletMode ? 6 : 5,
              fontSize: tabletMode ? 9 : 8,
              lineHeight: 1,
              fontWeight: 500,
              color: "rgba(255,255,255,0.84)",
              letterSpacing: -0.1,
            }}
          >
            {directionArrow}
          </div>
        )}

        {player?.team ? (
          <div
            style={{
              position: "absolute",
              top: tabletMode ? 12 : 8,
              right: favorite ? (tabletMode ? 12 : 10) : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TeamLogo
              team={player.team}
              size={tabletMode ? 15.625 : 14.0625}
              fallback={<span style={{ fontSize: tabletMode ? 7 : 6, fontWeight: 700, color: "rgba(255,255,255,0.88)" }}>{displayTeam}</span>}
            />
          </div>
        ) : null}

        {player ? (
          <>
            <div
              style={{
                marginTop: tabletMode ? 9 : 7,
                paddingRight: favorite ? (tabletMode ? 34 : 29) : tabletMode ? 22 : 18,
                fontSize: tabletMode ? 8.5 : 7.5,
                lineHeight: 1,
                fontWeight: 650,
                color: "rgba(255,255,255,0.76)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                letterSpacing: -0.1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <span>{player.position}</span>
              <span style={{ opacity: 0.5 }}>-</span>
              <span>{displayTeam}</span>
            </div>

            <div
              style={{
                marginTop: tabletMode ? 4 : 3,
                paddingRight: favorite ? (tabletMode ? 18 : 16) : tabletMode ? 10 : 8,
                fontSize: tabletMode ? 9 : 8,
                lineHeight: tabletMode ? 1.02 : 1,
                fontWeight: 700,
                color: "rgba(255,255,255,0.97)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                gap: 0,
                minHeight: tabletMode ? 16 : 14,
                overflow: "hidden",
                letterSpacing: -0.08,
              }}
            >
              <span
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {nameLines[0]}
              </span>
              <span
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {nameLines[1]}
              </span>
            </div>
          </>
        ) : (
          <div
            style={{
              marginTop: "auto",
              fontSize: 18,
              lineHeight: 1,
              fontWeight: 700,
              color: "rgba(255,255,255,0.78)",
            }}
          >
            —
          </div>
        )}
      </div>
    </button>
  );
}

const MobileRankingsBoardCard = React.memo(MobileRankingsBoardCardComponent);

export { MobileRankingsBoardCard };
export default MobileRankingsBoardCard;
